import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type { Account, RealtimeEvent, SyncJob, SyncStatus } from '@/types/email';
import { normalizeProgress } from '@/lib/progress';

type AccountSyncSummary = {
  status: Account['status'];
  lastSyncedAt?: string | null;
  errorMessage?: string | null;
  progress: number | null;
  processedMessages: number;
  targetMessages: number | null;
};

type MailboxProgressEntry = {
  accountId: string;
  processed: number;
  total: number;
  status: string | null;
};

interface SyncStatusStoreState {
  syncStatuses: Record<string, AccountSyncSummary>;
  syncJobs: SyncJob[];
  syncTotals: {
    processed: number;
    total: number;
    progress: number | null;
  } | null;
  mailboxProgress: Map<string, MailboxProgressEntry>;
  syncTotalsSource: 'summary' | 'incremental' | null;

  syncProgressIndicators: () => Array<{
    label: string;
    percent: number | null;
    detail?: string;
    state: 'queued' | 'running' | 'failed';
  }>;
  syncingAccountIds: () => Set<string>;

  updateStatus: (accountId: string, status: AccountSyncSummary) => void;
  updateJob: (job: SyncJob) => void;
  updateProgressFromEvent: (event: Extract<RealtimeEvent, { type: 'mailbox.sync.progress' }>) => void;
  updateStatusFromEvent: (event: { status: SyncStatus }) => void;
  updateTotals: (totals: { processed: number; total: number; progress: number | null }, source?: 'summary' | 'incremental') => void;
}

function summarizeSyncProgress(syncStatuses: Record<string, AccountSyncSummary>) {
  const aggregate = Object.values(syncStatuses).reduce(
    (acc, summary) => {
      acc.processed += summary.processedMessages ?? 0;
      acc.total += summary.targetMessages ?? 0;
      return acc;
    },
    { processed: 0, total: 0 }
  );
  const progress = aggregate.total > 0 ? Math.min(1, aggregate.processed / aggregate.total) : null;
  return {
    processed: aggregate.processed,
    total: aggregate.total,
    progress,
  };
}

export const useSyncStatusStore = create<SyncStatusStoreState>()(
  conditionalDevtools(
    (set, get) => ({
      syncStatuses: {},
      syncJobs: [],
      syncTotals: null,
      mailboxProgress: new Map(),
      syncTotalsSource: null,

      syncProgressIndicators: () => {
        const indicators: ReturnType<SyncStatusStoreState['syncProgressIndicators']> = [];
        const { syncTotals, syncJobs } = get();
        const activeTotals = syncTotals && syncTotals.progress !== null && syncTotals.progress < 0.999;
        if (activeTotals && syncTotals) {
          indicators.push({
            label: `Sync progress`,
            percent: syncTotals.progress,
            detail:
              syncTotals.total > 0
                ? `${syncTotals.processed.toLocaleString()} / ${syncTotals.total.toLocaleString()}`
                : undefined,
            state: 'running',
          });
        }
        syncJobs.forEach((job) => {
          if (job.state === 'queued' || job.state === 'running') {
            indicators.push({
              label: job.jobType.replace(/_/g, ' '),
              percent: null,
              state: job.state === 'queued' ? 'queued' : 'running',
            });
          }
        });
        return indicators;
      },

      syncingAccountIds: () => {
        const ids = new Set<string>();
        Object.entries(get().syncStatuses).forEach(([accountId, summary]) => {
          if (summary.status === 'syncing' || (summary.progress !== null && summary.progress < 0.999)) {
            ids.add(accountId);
          }
        });
        return ids;
      },

      updateStatus: (accountId, status) => {
        set((state) => {
          const next = { ...state.syncStatuses, [accountId]: status };
          if (state.syncTotalsSource !== 'summary') {
            const totals = summarizeSyncProgress(next);
            return { syncStatuses: next, syncTotals: totals, syncTotalsSource: 'incremental' };
          }
          return { syncStatuses: next };
        });
      },

      updateJob: (job) => {
        set((state) => {
          const map = new Map(state.syncJobs.map((entry) => [entry.id, entry]));
          map.set(job.id, job);
          const filtered = Array.from(map.values()).filter((entry) => entry.state !== 'completed');
          return { syncJobs: filtered };
        });
      },

      updateProgressFromEvent: (event) => {
        const accountId = String(event.accountId ?? '').trim();
        const mailboxId = String(event.mailboxId ?? '').trim();
        if (!accountId || !mailboxId) {
          return;
        }
        const processed = Number(event.processed ?? 0);
        const total = Number(event.total ?? 0);
        const status = typeof event.status === 'string' ? event.status : null;
        const key = `${accountId}:${mailboxId}`;

        set((state) => {
          const nextMap = new Map(state.mailboxProgress);
          const isComplete = status === 'complete' || (total > 0 && processed >= total);
          if (isComplete) {
            nextMap.delete(key);
          } else {
            nextMap.set(key, {
              accountId,
              processed: Number.isFinite(processed) ? Math.max(0, processed) : 0,
              total: Number.isFinite(total) ? Math.max(0, total) : 0,
              status,
            });
          }
          return { mailboxProgress: nextMap };
        });

        const map = get().mailboxProgress;
        const entries = Array.from(map.values()).filter((entry) => entry.accountId === accountId);
        if (!entries.length) {
          // The FINAL mailbox for this account just completed (its entry was
          // deleted above). Without this, the account summary stays "syncing"
          // until the next full snapshot (E10).
          const previous = get().syncStatuses[accountId];
          if (previous && previous.status === 'syncing') {
            get().updateStatus(accountId, {
              ...previous,
              status: 'idle',
              progress: 1,
            });
          }
          return;
        }
        const aggregate = entries.reduce(
          (acc, entry) => {
            acc.processed += entry.processed;
            acc.total += entry.total;
            if (entry.status === 'failed') {
              acc.failures = true;
            }
            const complete = entry.status === 'complete' || (entry.total > 0 && entry.processed >= entry.total);
            if (!complete) {
              acc.completed = false;
            }
            return acc;
          },
          { processed: 0, total: 0, failures: false, completed: true }
        );

        const progress =
          aggregate.total > 0
            ? Math.min(1, aggregate.processed / aggregate.total)
            : aggregate.completed
              ? 1
              : null;

        const nextStatus: Account['status'] = aggregate.failures
          ? 'error'
          : aggregate.completed && (progress === null || progress >= 0.999)
            ? 'idle'
            : 'syncing';

        const previous = get().syncStatuses[accountId];
        const summary: AccountSyncSummary = {
          status: nextStatus,
          lastSyncedAt: previous?.lastSyncedAt ?? null,
          errorMessage: aggregate.failures ? 'Sync error' : previous?.errorMessage ?? null,
          progress,
          processedMessages: aggregate.processed,
          targetMessages: aggregate.total || null,
        };
        get().updateStatus(accountId, summary);
      },

      updateStatusFromEvent: (event) => {
        if (!event?.status) {
          return;
        }
        const processed = Number(event.status?.totals?.processedMessages ?? 0);
        const total = Number(event.status?.totals?.targetMessages ?? 0);
        const progress = normalizeProgress(event.status?.overallProgress ?? null, processed, total);

        set({
          syncTotals: {
            processed: Math.max(0, processed),
            total: Math.max(0, total),
            progress,
          },
          syncTotalsSource: 'summary',
        });

        if (Array.isArray(event.status.jobs)) {
          const jobs = event.status.jobs ?? [];
          set({
            syncJobs: jobs.filter((job) => job.state !== 'completed'),
          });
        }

        if (Array.isArray(event.status.accounts)) {
          const summaries = (event.status.accounts ?? []).reduce<Record<string, AccountSyncSummary>>(
            (acc, entry) => {
              if (!entry) {
                return acc;
              }
              const processedMessages = Number(entry.processedMessages ?? 0);
              const targetMessages =
                typeof entry.targetMessages === 'number'
                  ? entry.targetMessages
                  : Number(entry.targetMessages ?? 0) || null;
              const normalized = normalizeProgress(entry.progress ?? null, processedMessages, targetMessages ?? 0);

              acc[entry.accountId] = {
                status: entry.status,
                lastSyncedAt: entry.lastSyncedAt ?? null,
                errorMessage: entry.errorMessage ?? null,
                progress: normalized,
                processedMessages,
                targetMessages,
              };
              return acc;
            },
            {}
          );
          set({ syncStatuses: summaries });
        }
      },

      updateTotals: (totals, source = 'incremental') => {
        set({
          syncTotals: totals,
          syncTotalsSource: source,
        });
      },
    }),
    { name: 'SyncStatusStore' }
  )
);
